import React, { FC } from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { ImageDataLike } from 'gatsby-plugin-image';
import { Button } from '../button';
import { Container } from '../container';
import { Spacer } from '../spacer';
import { Typography } from '../typography';
import { Image } from '../Image/Image';
import { createMarkup, removeHtml } from '../../utils';
import HtmlContent from '../htmlContent/HtmlContent';

// Interface

export interface SplashHeroProps {
  image?: { image: { localFile: ImageDataLike } };
  subHeading?: string;
  heading: string;
  description?: string;
  buttonOne?: ButtonProps;
  removeImageFilter?: boolean;
  makeTextBlack?: boolean;
}

export interface ButtonProps {
  url?: string;
  label?: string;
  onClick?: any;
}

// Styles

const StyledSplashHero = styled.div`
  ${({ theme: { gradients }, hasImage, removeImageFilter }) => css`
    position: relative;

    ${!removeImageFilter &&
    css`
      &:after {
        content: '';
        display: block;
        width: 100%;
        height: 100%;
        position: absolute;
        top: 0;
        left: 0;

        ${hasImage &&
        css`
          background: ${gradients.heroOverlay};
        `};

        ${!hasImage &&
        css`
          background: ${gradients.brandGreenRed};
        `};
      }
    `};
  `};
`;

const StyledImage = styled.div`
  ${({ theme: { layers } }) => css`
    .gatsby-image-wrapper {
      height: 100%;
    }

    position: absolute;
    left: 0;
    top: 0;
    z-index: ${layers.heroBackgroundImage};
    height: 100%;
    width: 100%;
    object-fit: cover;
  `};
`;

const StyledContainer = styled(Container)`
  ${() => css`
    display: flex;
    flex-direction: column;
    height: 100%;
    justify-content: center;
    align-items: center;
  `}
`;

const StyledBody = styled(Spacer)`
  ${({ theme: { space, media, layers } }) => css`
    position: relative;
    z-index: ${layers.heroBody};
    text-align: center;
    padding: ${space.medium} 0;

    @media (min-width: ${media.medium}) {
      padding: ${space.xLarge} 0;
    }

    @media (min-width: ${media.large}) {
      padding: ${space.xxxLarge} 0;
    }
  `};
`;

const StyledButtons = styled.div`
  ${({ theme: { space, media, maxMedia } }) => css`
    display: flex;
    gap: ${space.xSmall};
    justify-content: center;

    @media (max-width: ${maxMedia.medium}) {
      flex-direction: column;

      > * {
        width: 100%;
      }
    }

    @media (min-width: ${media.medium}) {
      flex-direction: row;
    }
  `};
`;

const StyledHeading = styled(HtmlContent)`
  ${({ theme: { colors, space, media, maxMedia }, makeTextBlack }) => css`
    color: ${makeTextBlack ? colors.neutralGrey : colors.neutralWhite};
    max-width: 580px;
  `};
`;

// JSX

export const SplashHero: FC<SplashHeroProps> = ({
  image,
  subHeading,
  heading,
  description,
  buttonOne,
  removeImageFilter,
  makeTextBlack,
}) => (
  <StyledSplashHero hasImage={!!image} removeImageFilter={removeImageFilter}>
    {image && (
      <StyledImage>
        <Image
          src={image.image.localFile}
          alt={removeHtml(heading)}
          loading="eager"
        />
      </StyledImage>
    )}
    <StyledContainer>
      <StyledBody>
        <Spacer spacing="small">
          {subHeading && (
            <Typography
              component="p"
              variant="caps"
              color={makeTextBlack ? 'neutralGrey' : 'neutralWhite'}
            >
              {subHeading}
            </Typography>
          )}
          {heading && (
            <StyledHeading makeTextBlack={makeTextBlack}>
              {createMarkup(heading)}
            </StyledHeading>
          )}
          {description && (
            <Typography
              component="p"
              variant="headingMediumSmall"
              color={makeTextBlack ? 'neutralGrey' : 'neutralWhite'}
            >
              {description}
            </Typography>
          )}
        </Spacer>
        <StyledButtons>
          {buttonOne && <Button variant="primary" {...buttonOne} />}
        </StyledButtons>
      </StyledBody>
    </StyledContainer>
  </StyledSplashHero>
);

export default SplashHero;
